import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import LeaseEventTable from "../LeaseEventsTable";

import { Container } from "./styles";

import { getRelatedLeaseEvents } from "redux/actions/leaseActions";
import { getLeaseEvent } from "redux/actions/leaseEventActions";

import moment from "moment";
import { round } from "components/CommonFns";
import { formatNumberWithCommas } from "shared/utils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75vw",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  height: "85vh",
  overflow: "scroll",
  p: 4,
};

const DetailsModal = (props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const relatedLeaseEvents = useSelector(
    (state) => state.leases.relatedLeaseEvents
  );
  const details = useSelector((state) => state.leaseEvents.details);
  const { open, onClose, selectedLeaseEventId } = props;

  const getLeaseEventDetails = () => {
    setLoading(true);

    dispatch(getLeaseEvent(selectedLeaseEventId))
      .catch((e) => {
        setError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedLeaseEventId) {
      getLeaseEventDetails();
    }
  }, [selectedLeaseEventId]);

  useEffect(() => {
    if (details && details.leaseId) {
      setLoading(true);

      dispatch(getRelatedLeaseEvents(details.leaseId))
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [details]);

  const renderDetailItem = (label, value) => {
    return (
      <>
        <Box
          sx={{
            display: "inline-block",
            width: "30%",
            "margin-bottom": "20px",
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            display: "inline-block",
            width: "70%",
            "font-weight": "bold",
          }}
        >
          {value || "-"}
        </Box>
      </>
    );
  };

  if (!details) return null;

  const {
    leaseName,
    eventType,
    floor,
    tenureType,
    eventId,
    leaseCode,
    leaseReportUrl,
    propertyName,
    streetAddress1,
    streetAddress2,
    rentableBuildingArea,
    uomNameShort,
    ownershipType,
    propertyId,
    latitude,
    longitude,
    region,
    countryName,
    stateProvince,
    city,
    postalCode,
    propertyCode,
    eventNote,
    expireDate,
    commenceDate,
  } = details;

  const getLeaseTerm = () => {
    return expireDate && commenceDate
      ? moment(expireDate).diff(moment(commenceDate), "months") + " Months"
      : "-";
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Container>
        <Box sx={style}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 15,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            LEASE EVENT DETAILS
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <h3>Lease</h3>
                {renderDetailItem("Lease Name", leaseName)}
                {renderDetailItem("Event Type", eventType)}
                {renderDetailItem("Lease Term", getLeaseTerm())}
                {renderDetailItem("Tenure Type", tenureType)}
                {renderDetailItem("Event Id", eventId)}
                {renderDetailItem("Tenure Id", leaseCode)}
                {renderDetailItem(
                  "Lease Report",
                  <a href={leaseReportUrl} target="blank">
                    Link
                  </a>
                )}
              </Grid>
              <Grid item xs={8}>
                <h3>Property</h3>
                <Box sx={{ display: "inline-block", width: "50%" }}>
                  {renderDetailItem("Property Name", propertyName)}
                  {renderDetailItem("Street Address 1", streetAddress1)}
                  {renderDetailItem("Street Address 2", streetAddress2)}
                  {renderDetailItem(
                    "Building Area",
                    rentableBuildingArea
                        ? formatNumberWithCommas(round(rentableBuildingArea,2)) + " " + uomNameShort
                        : ""
                  )}
                  {renderDetailItem("Ownership Type", ownershipType)}
                  {renderDetailItem("Property ID", propertyCode)}
                  {renderDetailItem("Latitude", latitude)}
                </Box>
                <Box sx={{ display: "inline-block", width: "50%" }}>
                  {renderDetailItem("Region", region)}
                  {renderDetailItem("Country", countryName)}
                  {renderDetailItem("State / Province", stateProvince)}
                  {renderDetailItem("City", city)}
                  {renderDetailItem("Postal / Zip Code", postalCode)}
                  {renderDetailItem("Longitude", longitude)}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <h3>Event Note</h3>
                {eventNote || "-"}
              </Grid>
              <Grid item xs={12}>
                <h3>Related Lease Events</h3>
                <LeaseEventTable
                  loading={loading}
                  leaseEvents={relatedLeaseEvents}
                  hideToolbar={true}
                  totalLeaseEvents={relatedLeaseEvents.length}
                  hideColumns={[
                    "leaseName",
                    "associatedTransactions",
                    "leaseEventId",
                    "actions",
                  ]}
                  showCheckbox={false}
                  pageSize={10}
                  onPageChange={()=>{}}
                />
              </Grid>
            </Grid>
          </Typography>
        </Box>
      </Container>
    </Modal>
  );
};

export default DetailsModal;
